/**
 * PHASE 4 — orientation fusion service.
 *
 * Consumes deviceorientation (/deviceorientationabsolute) events from any
 * source (window on device; a mock in tests), converts each reading to the
 * device-to-earth quaternion (W3C formula), applies the calibration yaw
 * offset, optionally smooths, and exposes the latest orientation to the app.
 *
 * The "camera" frame handed to projection (phase 5) is:
 *
 *   q_cam : world(ENU) -> device frame   =  conj(applyCalibration(q_dev2earth))
 *
 * i.e. rotateVector(v_world, q_cam) gives the direction in device
 * coordinates, x right / y top / z out of screen. The back camera looks along
 * the device -z axis; the projection phase maps that into clip space.
 *
 * Screen orientation angle (portrait/landscape) does NOT enter this math:
 * DeviceOrientation alphas are reported in the physical (sensor) frame, so a
 * spherical sky view needs no screen-angle correction.
 */

import { CalibrationStore } from "./compassCalibration";
import { applyCalibration } from "./compassCalibration";
import { deviceToEarthQ, type OrientationReading } from "./deviceOrientation";
import type { Quat } from "./quaternion";
import { normalize, smooth } from "./quaternion";

/** The subset of the event shape we use (duck type, no DOM dependency). */
export interface OrientationEventLike {
  alpha: number | null;
  beta: number | null;
  gamma: number | null;
  absolute: boolean;
  timeStamp?: number;
}

export interface OrientationSource {
  addEventListener(type: "deviceorientation" | "deviceorientationabsolute", cb: (e: OrientationEventLike) => void): void;
  removeEventListener(type: "deviceorientation" | "deviceorientationabsolute", cb: (e: OrientationEventLike) => void): void;
}

export interface OrientationFusionOptions {
  /** 0..1; 1 = no smoothing (raw), 0 = frozen. Default 0.6. */
  smoothing?: number;
  /** Calibration store; defaults to a null (no-offset) store. */
  calibration?: CalibrationStore;
}

export type OrientationStatus = "unavailable" | "no-data" | "relative" | "absolute";

export class OrientationFusion {
  private source: OrientationSource | null;
  private opts: Required<Pick<OrientationFusionOptions, "smoothing">> & OrientationFusionOptions;
  private calibration: CalibrationStore;
  private listening = false;
  /** Latest orientation: device-to-earth (calibrated, smoothed), or null. */
  private q: Quat | null = null;
  private lastReadingValue: OrientationReading | null = null;
  private currentStatus: OrientationStatus = "unavailable";
  /** True once an absolute (true-north referenced) reading arrived. */
  private sawAbsolute = false;
  private listeners = new Set<(q: Quat | null) => void>();

  constructor(source: OrientationSource | null, options: OrientationFusionOptions = {}) {
    this.source = source;
    this.opts = { smoothing: clamp01(options.smoothing ?? 0.6), ...options };
    this.calibration = options.calibration ?? new CalibrationStore(null);
  }

  private handler = (e: OrientationEventLike): void => {
    // Chrome and iOS Safari deliver BOTH deviceorientation (relative frame,
    // alpha anchored to an arbitrary reference) and
    // deviceorientationabsolute (true-north frame) at the same rate. The
    // relative frame's alpha does not reference north and would make the sky
    // point a random direction, so once a true absolute reading has been
    // seen, relative readings are ignored entirely.
    if (!e.absolute && this.sawAbsolute) return;
    if (e.absolute) this.sawAbsolute = true;
    const reading: OrientationReading = {
      alphaDeg: e.alpha,
      betaDeg: e.beta,
      gammaDeg: e.gamma,
      absolute: e.absolute,
      timestampMs: e.timeStamp ?? Date.now(),
    };
    this.lastReadingValue = reading;
    const raw = deviceToEarthQ(reading);
    if (!raw) {
      // Sensor thinks there is no meaningful data (all-null event).
      return;
    }
    this.currentStatus = reading.absolute ? "absolute" : "relative";
    const corrected = applyCalibration(raw, this.calibration.getOffsetRad());
    const q =
      this.q === null ? normalize(corrected) : smooth(this.q, normalize(corrected), this.opts.smoothing);
    this.q = q;
    if (this.listening) {
      for (const listener of this.listeners) listener(q);
    }
  };

  start(): void {
    if (this.listening || !this.source) return;
    this.source.addEventListener("deviceorientationabsolute", this.handler);
    this.source.addEventListener("deviceorientation", this.handler);
    this.listening = true;
  }

  stop(): void {
    if (!this.listening || !this.source) return;
    this.source.removeEventListener("deviceorientationabsolute", this.handler);
    this.source.removeEventListener("deviceorientation", this.handler);
    this.listening = false;
  }

  get isListening(): boolean {
    return this.listening;
  }

  get status(): OrientationStatus {
    return this.currentStatus;
  }

  /** Device-to-earth quaternion (calibrated + smoothed), or null. */
  get qDeviceToEarth(): Quat | null {
    return this.q;
  }

  /** Camera frame: world(ENU) -> device. Null until first reading. */
  get qCamera(): Quat | null {
    if (!this.q) return null;
    return { x: -this.q.x, y: -this.q.y, z: -this.q.z, w: this.q.w };
  }

  get lastReading(): OrientationReading | null {
    return this.lastReadingValue;
  }

  onChange(listener: (q: Quat | null) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Inject a synthetic reading (test harness / future manual mode). */
  inject(reading: OrientationEventLike): void {
    this.handler(reading);
  }
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}